import React from 'react';
import { createPortal } from 'react-dom';
import { ShieldAlert, AlertTriangle, RotateCcw, RefreshCw, X } from 'lucide-react';

interface RestoreBackupModalProps {
  isOpen: boolean;
  filename: string | null;
  isRestoring: boolean;
  onClose: () => void;
  onConfirmRestore: () => void;
}

export const RestoreBackupModal: React.FC<RestoreBackupModalProps> = ({
  isOpen,
  filename,
  isRestoring,
  onClose,
  onConfirmRestore,
}) => {
  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-md w-full p-4 sm:p-5 shadow-2xl border border-rose-100 space-y-3 animate-in zoom-in-95 duration-150 relative my-auto">
        <button
          type="button"
          onClick={onClose}
          disabled={isRestoring}
          className="absolute top-3.5 right-3.5 p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2.5 text-rose-600 pr-6">
          <div className="p-2 rounded-xl bg-rose-50 border border-rose-200 shrink-0">
            <ShieldAlert className="w-5 h-5 text-rose-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm sm:text-base font-bold text-slate-900 leading-tight">
              Konfirmasi Restore Database
            </h3>
            <p className="text-[11px] text-rose-600 font-medium">Tindakan ini berbahaya & permanen</p>
          </div>
        </div>

        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 space-y-1.5">
          <p className="font-semibold flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
            Peringatan Penting:
          </p>
          <p className="leading-relaxed text-[11px]">
            Proses restore akan mengganti seluruh data pengguna, naskah buku, histori pemindaian, dan FTS5 dengan data dari berkas cadangan:
          </p>
          <p className="font-mono text-[11px] font-bold text-indigo-900 bg-white px-2 py-1 rounded border border-amber-300 break-all leading-snug">
            {filename}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isRestoring}
            className="px-3.5 py-1.5 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 transition cursor-pointer"
          >
            Batal
          </button>

          <button
            type="button"
            onClick={onConfirmRestore}
            disabled={isRestoring}
            className="px-3.5 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold shadow-xs flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50"
          >
            {isRestoring ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>Memulihkan...</span>
              </>
            ) : (
              <>
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Ya, Lanjutkan Restore</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
